<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import {
  COMPACT_BUTTON_CLASS as compactButtonClass,
  INPUT_CLASS as inputClass,
  PRIMARY_COMPACT_BUTTON_CLASS as primaryCompactButtonClass,
  SELECT_CLASS as selectClass,
} from '../utils/pendentesConstants';
import { number } from '../utils/pendentesFormatters';

defineProps({
  statusOptions: { type: Array, default: () => [] },
  activeFiltersCount: { type: Number, default: 0 },
  activeFilterChips: { type: Array, default: () => [] },
  showFilters: { type: Boolean, default: false },
});

const emit = defineEmits(['apply', 'reset', 'removeFilter']);

const filters = defineModel('filters', { type: Object, required: true });
</script>

<template>
  <form
    class="w-full min-w-0 max-w-full shrink-0 overflow-x-hidden border-b border-lais-border bg-white px-4 py-3 sm:px-6 lg:px-10"
    @submit.prevent="emit('apply')"
  >
    <div
      class="grid min-w-0 gap-2 xl:grid-cols-[minmax(18rem,1fr)_auto] xl:items-center"
    >
      <label class="relative min-w-0 flex-1">
        <span
          class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
          aria-hidden="true"
        />
        <input
          v-model="filters.q"
          class="h-10 w-full rounded-lais border border-lais-border bg-white pl-10 pr-3 text-[14px] text-lais-text outline-none transition placeholder:text-lais-text-muted focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
          placeholder="Buscar endereço, proprietário, telefone, código ou portal"
        />
      </label>

      <div
        class="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:flex xl:flex-wrap xl:justify-end"
      >
        <select
          v-model="filters.status"
          class="sm:w-56 xl:w-52"
          :class="selectClass"
        >
          <option value="">Todos os status</option>
          <option
            v-for="statusItem in statusOptions"
            :key="statusItem.status"
            :value="statusItem.status"
          >
            {{ statusItem.label }} ({{ number(statusItem.count) }})
          </option>
        </select>
        <select
          v-model="filters.sentState"
          class="sm:w-44 xl:w-40"
          :class="selectClass"
        >
          <option value="">Envio: todos</option>
          <option value="sent">Com envio</option>
          <option value="not_sent">Sem envio</option>
        </select>
        <button
          type="submit"
          class="col-span-2 sm:col-span-1"
          :class="primaryCompactButtonClass"
        >
          <span class="i-lucide-search size-4" aria-hidden="true" />
          Buscar
        </button>
        <button
          type="button"
          :class="compactButtonClass"
          :disabled="!activeFiltersCount"
          @click="emit('reset')"
        >
          <span class="i-lucide-rotate-ccw size-4" aria-hidden="true" />
          Limpar
        </button>
      </div>
    </div>

    <div
      v-if="activeFilterChips.length"
      class="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar"
    >
      <button
        v-for="chip in activeFilterChips"
        :key="chip.key"
        type="button"
        class="inline-flex shrink-0 items-center gap-2 rounded-full border border-lais-border bg-lais-bg-page px-3 py-1.5 text-[12px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
        @click="emit('removeFilter', chip.key)"
      >
        {{ chip.label }}
        <span class="i-lucide-x size-3.5" aria-hidden="true" />
      </button>
    </div>
  </form>

  <LaisFilterBar :show="showFilters" class="shrink-0">
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:min-w-[170px] sm:flex-1"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Origem
      </span>
      <input
        v-model="filters.sourceKind"
        :class="inputClass"
        placeholder="Portal ou origem"
        @keyup.enter="emit('apply')"
      />
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:min-w-[170px] sm:flex-1"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Bairro
      </span>
      <input
        v-model="filters.neighbourhood"
        :class="inputClass"
        placeholder="Ex.: Botafogo"
        @keyup.enter="emit('apply')"
      />
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:min-w-[170px] sm:flex-1"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Cidade
      </span>
      <input
        v-model="filters.city"
        :class="inputClass"
        placeholder="Ex.: Rio de Janeiro"
        @keyup.enter="emit('apply')"
      />
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[150px]"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        De
      </span>
      <input v-model="filters.updatedSince" type="date" :class="inputClass" />
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[150px]"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Até
      </span>
      <input v-model="filters.updatedUntil" type="date" :class="inputClass" />
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[190px]"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Ordenar por
      </span>
      <select v-model="filters.sortBy" :class="selectClass">
        <option value="processedAt">Processamento</option>
        <option value="address">Logradouro</option>
        <option value="neighbourhood">Bairro</option>
        <option value="competitorPrice">Valor concorrente</option>
        <option value="housePrice">Valor na casa</option>
        <option value="status">Status</option>
      </select>
    </label>
    <label
      class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[120px]"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Direção
      </span>
      <select v-model="filters.sortDirection" :class="selectClass">
        <option value="desc">Desc.</option>
        <option value="asc">Asc.</option>
      </select>
    </label>
    <button
      type="button"
      class="w-full sm:w-auto"
      :class="primaryCompactButtonClass"
      @click="emit('apply')"
    >
      Aplicar filtros
    </button>
  </LaisFilterBar>
</template>
